import { useMemo, useState } from "react";
import { AlertTriangle, Check, ClipboardCheck, Download, UploadCloud, X } from "lucide-react";
import {
  api,
  type OrderProgressImportCandidate,
  type OrderProgressImportPreview,
  type OrderProgressImportRow,
} from "./appApi";

type Props = {
  capabilities: Record<string, boolean>;
  brands: string[];
  campuses: string[];
  close: () => void;
  done: (message: string) => void;
  toast: (message: string) => void;
};

const importStages = [
  { key: "photographed", label: "已拍摄" },
  { key: "retouched", label: "已修图" },
  { key: "layout_completed", label: "已排版" },
  { key: "factory_sent", label: "已送厂" },
  { key: "production_completed", label: "已制作" },
] as const;

type ParsedRow = { row_no: number; child_name: string; report_no?: string; class_name?: string };
type RowStatus = OrderProgressImportRow["status"];

function parseNames(text: string): ParsedRow[] {
  const result: ParsedRow[] = [];
  text.split(/\r?\n/).forEach((source, index) => {
    const line = source.trim();
    if (!line) return;
    let columns = line.includes("\t") ? line.split("\t") : line.includes(",") || line.includes("，") ? line.split(/[,，]/) : line.split(/\s+/);
    columns = columns.map(value => value.trim()).filter(Boolean);
    if (!columns.length) return;
    if (index === 0 && /^(姓名|孩子姓名|小孩姓名)$/.test(columns[0])) return;
    const childName = columns[0] || "";
    let reportNo = columns[1] || "";
    let className = columns[2] || "";
    if (reportNo && /班$/.test(reportNo) && !className) {
      className = reportNo;
      reportNo = "";
    }
    result.push({
      row_no: index + 1,
      child_name: childName,
      report_no: reportNo || undefined,
      class_name: className || undefined,
    });
  });
  return result.slice(0, 1000);
}

function selectedCandidate(row: OrderProgressImportRow): OrderProgressImportCandidate | undefined {
  return row.candidates.find(candidate => candidate.id === Number(row.order_report_id));
}

function effectiveRow(row: OrderProgressImportRow): { status: RowStatus; message: string } {
  if (["invalid", "duplicate", "unmatched"].includes(row.status)) return { status: row.status, message: row.message };
  if (!row.order_report_id) return { status: "ambiguous", message: "发现多个同名订单，请人工选择" };
  const candidate = selectedCandidate(row);
  if (!candidate) return { status: "unmatched", message: "选择的订单已不在候选范围" };
  if (candidate.target_completed) return { status: "already_completed", message: "该状态已经完成，本次自动跳过" };
  if (candidate.missing_upstream.length) {
    const names = candidate.missing_upstream_names.join("、");
    return { status: "matched", message: `将自动补齐${names}，再更新目标状态` };
  }
  return { status: "matched", message: row.status === "ambiguous" ? "已人工选择订单" : "匹配成功，可以更新" };
}

const statusLabels: Record<RowStatus, string> = {
  matched: "可更新",
  ambiguous: "待选择",
  unmatched: "未匹配",
  duplicate: "名单重复",
  invalid: "格式错误",
  already_completed: "已经完成",
  prerequisite_missing: "缺少前置",
};

export default function OrderProgressImportModal({ capabilities, brands, campuses, close, done, toast }: Props) {
  const allowedStages = importStages.filter(stage => capabilities[stage.key]);
  const [stage, setStage] = useState(allowedStages[0]?.key || "photographed");
  const [text, setText] = useState("");
  const [filters, setFilters] = useState({ brand: "", campus: "", class_name: "" });
  const [preview, setPreview] = useState<OrderProgressImportPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<RowStatus | "">("");
  const parsedRows = useMemo(() => parseNames(text), [text]);

  const displayRows = useMemo(() => (preview?.rows || []).map(row => ({ row, result: effectiveRow(row) })), [preview]);
  const counts = useMemo(() => {
    const value = Object.fromEntries(Object.keys(statusLabels).map(key => [key, 0])) as Record<RowStatus, number>;
    displayRows.forEach(item => { value[item.result.status] += 1; });
    return value;
  }, [displayRows]);
  const committable = displayRows.filter(item => item.result.status === "matched" && item.row.order_report_id);
  const visibleRows = statusFilter ? displayRows.filter(item => item.result.status === statusFilter) : displayRows;

  const changeInput = (value: string) => {
    setText(value);
    setPreview(null);
    setStatusFilter("");
  };

  const changeFilter = (key: keyof typeof filters, value: string) => {
    setFilters(current => ({ ...current, [key]: value }));
    setPreview(null);
    setStatusFilter("");
  };

  const runPreview = async () => {
    if (!parsedRows.length) return toast("请先粘贴孩子名单");
    setPreviewing(true);
    try {
      const result = await api.previewOrderProgressImport({ stage, items: parsedRows, filters });
      setPreview(result);
      setStatusFilter("");
    } catch (error) {
      toast((error as Error).message || "名单匹配预览失败");
    } finally {
      setPreviewing(false);
    }
  };

  const chooseOrder = (rowNo: number, orderId: number) => {
    setPreview(current => current ? {
      ...current,
      rows: current.rows.map(row => row.row_no === rowNo
        ? { ...row, order_report_id: orderId || null, status: orderId ? "matched" : "ambiguous", message: orderId ? "已人工选择订单" : "请选择订单" }
        : row),
    } : current);
  };

  const commit = async () => {
    if (!committable.length) return toast("没有可以更新的匹配订单");
    const stageName = importStages.find(item => item.key === stage)?.label || "目标状态";
    if (!window.confirm(`确认把 ${committable.length} 笔订单设置为“${stageName}”吗？\n系统会自动补齐缺少的前置进度；未匹配、重名和已完成记录不会修改。`)) return;
    setCommitting(true);
    try {
      const result = await api.commitOrderProgressImport({
        stage,
        cascade_upstream: true,
        items: committable.map(({ row }) => ({
          row_no: row.row_no,
          child_name: row.child_name,
          report_no: row.report_no || undefined,
          class_name: row.class_name || undefined,
          order_report_id: row.order_report_id,
        })),
      });
      done(`成功更新 ${result.updated} 笔订单${result.cascade_updated ? `，补齐 ${result.cascade_updated} 个前置进度` : ""}${result.skipped.length ? `，跳过 ${result.skipped.length} 笔` : ""}`);
    } catch (error) {
      toast((error as Error).message || "批量进度更新失败");
    } finally {
      setCommitting(false);
    }
  };

  const exportExceptions = () => {
    const exceptions = displayRows.filter(item => item.result.status !== "matched" && item.result.status !== "already_completed");
    if (!exceptions.length) return toast("当前没有需要导出的异常名单");
    const content = ["行号\t姓名\t订单号\t班级\t结果", ...exceptions.map(({ row, result }) => [row.row_no, row.child_name, row.report_no || "", row.class_name || "", result.message].join("\t"))].join("\n");
    const blob = new Blob(["\ufeff", content], { type: "text/tab-separated-values;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `订单进度异常名单-${new Date().toISOString().slice(0, 10)}.tsv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return <div className="modal-backdrop">
    <div className="modal production-import-modal">
      <div className="modal-head">
        <div><span className="eyebrow">BATCH PROGRESS</span><h2>批量导入订单进度</h2><p>粘贴名单后先预览匹配；同名、重复和未找到的订单不会自动修改。</p></div>
        <button className="icon-btn" onClick={close}><X size={18} /></button>
      </div>

      <div className="production-import-settings">
        <label><span>本批设置状态</span><select value={stage} onChange={event => { setStage(event.target.value as typeof stage); setPreview(null); setStatusFilter(""); }}>{allowedStages.map(item => <option key={item.key} value={item.key}>{item.label}</option>)}</select></label>
        <label><span>品牌范围</span><select value={filters.brand} onChange={event => changeFilter("brand", event.target.value)}><option value="">全部品牌</option>{brands.map(item => <option key={item} value={item}>{item}</option>)}</select></label>
        <label><span>校区范围</span><select value={filters.campus} onChange={event => changeFilter("campus", event.target.value)}><option value="">全部校区</option>{campuses.map(item => <option key={item} value={item}>{item}</option>)}</select></label>
        <label><span>班级范围</span><input value={filters.class_name} onChange={event => changeFilter("class_name", event.target.value)} placeholder="可选，减少重名" /></label>
      </div>

      <label className="production-import-paste"><span>从 Excel 复制名单后粘贴</span><textarea rows={7} value={text} onChange={event => changeInput(event.target.value)} placeholder={"支持格式：\n张小明\n李小雨\tBD202607090087\t二班\n王晓红\t三班\n\n列顺序：姓名、订单号（可选）、班级（可选）"} /></label>
      <div className="production-import-actions"><span>识别到 <b>{parsedRows.length}</b> 行，单次最多1000行</span><button className="primary" disabled={!parsedRows.length || previewing} onClick={runPreview}><ClipboardCheck size={16} />{previewing ? "正在匹配…" : "预览匹配结果"}</button></div>

      {preview && <>
        <div className="production-import-safety">
          <label><span><b>自动补齐前置进度</b><small>例：导入“已排版”时，系统会同步完成缺少的“已拍摄、已修图”，并为每个阶段记录操作日志。</small></span></label>
        </div>
        <div className="production-import-summary">
          {(Object.keys(statusLabels) as RowStatus[]).map(status => <button
            type="button"
            key={status}
            className={`${status} ${statusFilter === status ? "active" : ""}`}
            aria-pressed={statusFilter === status}
            title={`点击只看“${statusLabels[status]}”记录${statusFilter === status ? "，再次点击取消筛选" : ""}`}
            onClick={() => setStatusFilter(current => current === status ? "" : status)}
          ><b>{counts[status]}</b><small>{statusLabels[status]}</small><em>{statusFilter === status ? "筛选中" : "点击筛选"}</em></button>)}
        </div>
        {statusFilter && <div className="production-import-filter-note"><span>当前只显示：<b>{statusLabels[statusFilter]}</b>（{visibleRows.length} 条）</span><button type="button" onClick={() => setStatusFilter("")}><X size={13} />清除筛选</button></div>}
        <div className="production-import-preview"><table><thead><tr><th>行</th><th>导入名单</th><th>匹配订单</th><th>结果</th></tr></thead><tbody>{visibleRows.map(({ row, result }) => <tr key={row.row_no}>
          <td>{row.row_no}</td>
          <td><b>{row.child_name || "-"}</b><span>{row.report_no || "未填订单号"} · {row.class_name || "未填班级"}</span></td>
          <td>{row.candidates.length > 1 ? <select value={row.order_report_id || ""} onChange={event => chooseOrder(row.row_no, Number(event.target.value))}><option value="">请选择同名订单</option>{row.candidates.map(candidate => <option key={candidate.id} value={candidate.id}>{candidate.report_no} · {candidate.brand || "-"}/{candidate.campus || "-"}/{candidate.class_name || "-"}/{candidate.package_name || "-"}</option>)}</select> : row.candidates[0] ? <span><b>{row.candidates[0].report_no}</b>{row.candidates[0].brand || "-"}/{row.candidates[0].campus || "-"}/{row.candidates[0].class_name || "-"}</span> : "-"}</td>
          <td><span className={`production-import-status ${result.status}`}>{result.status === "matched" ? <Check size={13} /> : <AlertTriangle size={13} />}{result.message}</span></td>
        </tr>)}{!visibleRows.length && <tr><td colSpan={4}><div className="production-import-filter-empty">“{statusFilter ? statusLabels[statusFilter] : "当前"}”分类暂无记录</div></td></tr>}</tbody></table></div>
        <div className="production-import-footer">
          <button className="secondary" onClick={exportExceptions}><Download size={15} />导出异常名单</button>
          <span>本次将更新 <b>{committable.length}</b> 笔订单；其他记录全部跳过</span>
          <button className="primary" disabled={!committable.length || committing} onClick={commit}><UploadCloud size={16} />{committing ? "正在更新…" : "确认更新可匹配订单"}</button>
        </div>
      </>}
    </div>
  </div>;
}
